/**
 * Helpers for surfacing the clinical reference / embedded RAG documents that
 * back an LLM response, so they can be listed and previewed inline.
 *
 * Shared by ChartMind steps (via ClinicalReferenceSources) and the
 * Ask Me Anything chat.
 */

export const DEFAULT_MAX_SOURCES = 4;

/**
 * Turn a file name into a human-friendly label (drop extension, de-slug).
 * @param {{ fileName?: string }} source
 * @returns {string}
 */
export function displayLabel(source) {
  return (
    (source?.fileName || "Document")
      .replace(/\.(pdf|docx?|txt|md)$/i, "")
      .replace(/[_-]/g, " ")
      .trim() || "Document"
  );
}

/**
 * Remove inline {source: name} citation markers from an LLM response.
 *
 * The model is prompted to cite documents inline with `{source: ...}` markers,
 * but it tends to emit one after nearly every paragraph all pointing at the
 * same top document, which rendered as the same link repeated down the answer.
 * Attribution already lives in the "Source documents" footer, so we strip the
 * markers from the displayed text rather than rendering them as links.
 *
 * @param {string} content
 * @returns {string}
 */
export function stripSourceCitations(content) {
  if (!content) return content;
  return content
    .replace(/\{source:\s*[^}]*\}/gi, "")
    // Collapse any double spaces / space-before-punctuation left behind.
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+([.,;:)])/g, "$1")
    .trim();
}

/**
 * Strip a source list down to the fields needed for display, dropping
 * anything without a downloadURL. Used when persisting sources to a saved
 * session, where embedded documents would otherwise drag their full RAG
 * chunk text into Firestore.
 *
 * @param {Array<{ downloadURL?: string, fileName?: string, similarity?: number }>} docs
 * @returns {Array<{ downloadURL: string, fileName: string, similarity?: number }>}
 */
export function compactSourceList(docs) {
  return (docs || [])
    .filter((d) => d?.downloadURL)
    .map((d) => ({
      downloadURL: d.downloadURL,
      fileName: d.fileName || "Document",
      ...(typeof d.similarity === "number" && { similarity: d.similarity }),
    }));
}

/**
 * Merge embedded (RAG) documents and clinical references into a single,
 * de-duplicated list of source documents, ranked by similarity when known.
 *
 * @param {Array<{ downloadURL?: string, fileName?: string, similarity?: number }>} embeddedDocs
 * @param {Array<{ downloadURL?: string, fileName?: string }>} clinicalRefs
 * @param {number} [maxSources]
 * @returns {Array<{ downloadURL: string, fileName: string, similarity: number|null }>}
 */
export function mergeSourcesByUrl(
  embeddedDocs,
  clinicalRefs,
  maxSources = DEFAULT_MAX_SOURCES,
) {
  const byUrl = new Map();
  for (const doc of embeddedDocs || []) {
    if (!doc?.downloadURL) continue;
    byUrl.set(doc.downloadURL, {
      downloadURL: doc.downloadURL,
      fileName: doc.fileName || "Document",
      similarity: typeof doc.similarity === "number" ? doc.similarity : null,
    });
  }
  for (const ref of clinicalRefs || []) {
    if (!ref?.downloadURL || byUrl.has(ref.downloadURL)) continue;
    byUrl.set(ref.downloadURL, {
      downloadURL: ref.downloadURL,
      fileName: ref.fileName || "Document",
      similarity: null,
    });
  }
  return Array.from(byUrl.values())
    .sort((a, b) => (b.similarity ?? -1) - (a.similarity ?? -1))
    .slice(0, maxSources);
}
